import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate, todayIso } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, Factory, ArrowRight, Package, Layers } from "lucide-react";
import { toast } from "sonner";

const emptyInput = () => ({ product_id: "", quantity: 1 });

export default function Productions() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({
    production_date: todayIso(),
    output_product_id: "",
    output_quantity: 1,
    inputs: [emptyInput()],
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [{ data: prods }, { data: list }] = await Promise.all([
      api.get("/products", { params: { active_only: true } }),
      api.get("/productions"),
    ]);
    setProducts(prods);
    setItems(list);
  };
  useEffect(() => { load(); }, []);

  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));

  const openNew = () => {
    setForm({
      production_date: todayIso(),
      output_product_id: "",
      output_quantity: 1,
      inputs: [emptyInput()],
      notes: "",
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setInput = (i, k, v) => setForm((f) => {
    const inputs = [...f.inputs];
    inputs[i] = { ...inputs[i], [k]: v };
    return { ...f, inputs };
  });
  const addInput = () => setForm((f) => ({ ...f, inputs: [...f.inputs, emptyInput()] }));
  const removeInput = (i) => setForm((f) => ({
    ...f, inputs: f.inputs.length > 1 ? f.inputs.filter((_, x) => x !== i) : f.inputs,
  }));

  const save = async () => {
    if (!form.output_product_id) return toast.error("Üretilecek ürünü seçin");
    if (Number(form.output_quantity) <= 0) return toast.error("Üretilen miktar 0'dan büyük olmalı");
    for (const it of form.inputs) {
      if (!it.product_id) return toast.error("Tüm girdi ürünleri seçmelisiniz");
      if (Number(it.quantity) <= 0) return toast.error("Girdi miktarları 0'dan büyük olmalı");
    }
    setSaving(true);
    try {
      await api.post("/productions", {
        production_date: form.production_date,
        output_product_id: form.output_product_id,
        output_quantity: Number(form.output_quantity),
        inputs: form.inputs.map((it) => ({
          product_id: it.product_id,
          quantity: Number(it.quantity),
        })),
        notes: form.notes,
      });
      toast.success("Üretim kaydedildi. Stoklar güncellendi.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu üretim kaydını silmek stokları geri alır. Devam edilsin mi?")) return;
    try {
      await api.delete(`/productions/${id}`);
      toast.success("Silindi, stoklar geri alındı");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#6B7280]">
          Toplam <span className="font-bold text-[#08285A]">{items.length}</span> üretim kaydı
        </div>
        <Button onClick={openNew} data-testid="prod-new"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          <Plus size={16} className="mr-1" /> Yeni Üretim
        </Button>
      </div>

      <div className="azd-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Üretim No</th>
              <th className="text-left px-4 py-3 font-semibold">Tarih</th>
              <th className="text-left px-4 py-3 font-semibold">Üretilen Ürün</th>
              <th className="text-right px-4 py-3 font-semibold">Üretilen Miktar</th>
              <th className="text-center px-4 py-3 font-semibold">Girdi Sayısı</th>
              <th className="text-left px-4 py-3 font-semibold">Üreten</th>
              <th className="text-right px-4 py-3 font-semibold">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-[#6B7280]">
                <Factory size={28} className="mx-auto mb-2 opacity-40" />
                Henüz üretim kaydı yok
              </td></tr>
            ) : items.map((p) => (
              <tr key={p.id} data-testid={`prod-row-${p.id}`}
                  className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50 cursor-pointer"
                  onClick={() => setView(p)}>
                <td className="px-4 py-3 font-mono text-xs font-bold text-[#08285A]">{p.production_no}</td>
                <td className="px-4 py-3 text-xs">{fmtDate(p.production_date)}</td>
                <td className="px-4 py-3 font-semibold">{p.output_product_name}</td>
                <td className="px-4 py-3 text-right font-bold text-[#16A34A]">
                  +{p.output_quantity} {p.output_unit}
                </td>
                <td className="px-4 py-3 text-center">{(p.inputs || []).length}</td>
                <td className="px-4 py-3 text-xs">{p.created_by_name}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={(e) => { e.stopPropagation(); remove(p.id); }}
                          data-testid={`prod-del-${p.id}`}
                          className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New production dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory size={18} className="text-[#0B3676]" /> Yeni Üretim
            </DialogTitle>
            <DialogDescription>
              Kullanılan girdi ürünleri stoktan düşülecek, üretilen ürün stoğa eklenecek.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1.5">Üretim Tarihi</label>
                <Input type="date" value={form.production_date}
                       onChange={(e) => set("production_date", e.target.value)}
                       data-testid="prod-date" />
              </div>
            </div>

            {/* Inputs */}
            <div className="rounded-lg border border-[#E5E7EB] p-4">
              <div className="flex items-center gap-2 text-sm font-bold text-[#08285A] mb-3">
                <Package size={16} /> Kullanılan Girdi Ürünler (Stoktan Düşülecek)
              </div>
              <div className="space-y-2">
                {form.inputs.map((it, i) => {
                  const p = productMap[it.product_id];
                  const cur = p ? Number(p.current_stock || 0) : null;
                  const short = cur !== null && Number(it.quantity) > cur;
                  return (
                    <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2 items-start"
                         data-testid={`prod-input-${i}`}>
                      <div>
                        <Select value={it.product_id}
                                onValueChange={(v) => setInput(i, "product_id", v)}>
                          <SelectTrigger data-testid={`prod-input-select-${i}`}>
                            <SelectValue placeholder="Girdi ürün seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {products.filter(pp => pp.id !== form.output_product_id).map((pp) => (
                              <SelectItem key={pp.id} value={pp.id}>
                                {pp.product_name} ({pp.product_code}) — stok: {pp.current_stock || 0} {pp.unit}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {short && (
                          <div className="text-[11px] text-[#DC2626] mt-1 font-semibold">
                            Yetersiz stok! Mevcut: {cur} {p.unit}
                          </div>
                        )}
                      </div>
                      <Input type="number" min="0.01" step="0.01" value={it.quantity}
                             onChange={(e) => setInput(i, "quantity", e.target.value)}
                             className="text-right"
                             data-testid={`prod-input-qty-${i}`} placeholder="Miktar" />
                      <button type="button" onClick={() => removeInput(i)}
                              disabled={form.inputs.length === 1}
                              className="p-2 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded disabled:opacity-40">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addInput}
                      data-testid="prod-add-input" className="mt-3">
                <Plus size={12} className="mr-1" /> Girdi Ekle
              </Button>
            </div>

            <div className="flex items-center justify-center py-1">
              <ArrowRight size={22} className="text-[#0B3676]" />
            </div>

            {/* Output */}
            <div className="rounded-lg border border-[#0B3676] bg-[#F6F8FB] p-4">
              <div className="flex items-center gap-2 text-sm font-bold text-[#08285A] mb-3">
                <Layers size={16} /> Üretilen Ürün (Stoğa Eklenecek)
              </div>
              <div className="grid grid-cols-[1fr_140px] gap-2">
                <Select value={form.output_product_id}
                        onValueChange={(v) => set("output_product_id", v)}>
                  <SelectTrigger data-testid="prod-output"><SelectValue placeholder="Üretilecek ürünü seçin" /></SelectTrigger>
                  <SelectContent>
                    {products
                      .filter(pp => !form.inputs.some(it => it.product_id === pp.id))
                      .map((pp) => (
                        <SelectItem key={pp.id} value={pp.id}>
                          {pp.product_name} ({pp.product_code}) — mevcut stok: {pp.current_stock || 0} {pp.unit}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Input type="number" min="0.01" step="0.01" value={form.output_quantity}
                       onChange={(e) => set("output_quantity", e.target.value)}
                       className="text-right"
                       data-testid="prod-output-qty" placeholder="Üretilen Miktar" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1.5">Not (Opsiyonel)</label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                        rows={2} placeholder="Üretim ile ilgili notlar..." />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} disabled={saving} data-testid="prod-save"
                    className="bg-[#0B3676] hover:bg-[#08285A] text-white">
              {saving ? "Kaydediliyor..." : "Üretimi Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail view */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{view?.production_no}</DialogTitle>
          </DialogHeader>
          {view && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-[#6B7280]">Tarih:</span> <b>{fmtDate(view.production_date)}</b></div>
                <div><span className="text-[#6B7280]">Üreten:</span> <b>{view.created_by_name}</b></div>
              </div>
              <div className="p-3 bg-[#F6F8FB] rounded-lg">
                <div className="text-xs font-bold text-[#08285A] mb-2">GİRDİLER (Stoktan Düşüldü)</div>
                <ul className="text-sm space-y-1">
                  {(view.inputs || []).map((it, i) => (
                    <li key={i} className="flex justify-between">
                      <span>{it.product_name} ({it.product_code})</span>
                      <span className="font-bold text-[#DC2626]">−{it.quantity} {it.unit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-3 bg-[#F0FDF4] rounded-lg border border-[#16A34A]/30">
                <div className="text-xs font-bold text-[#166534] mb-2">ÇIKTI (Stoğa Eklendi)</div>
                <div className="flex justify-between text-sm">
                  <span>{view.output_product_name} ({view.output_product_code})</span>
                  <span className="font-bold text-[#16A34A]">+{view.output_quantity} {view.output_unit}</span>
                </div>
              </div>
              {view.notes && (
                <div>
                  <div className="text-xs text-[#6B7280] mb-1">Not:</div>
                  <div className="text-sm">{view.notes}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setView(null)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
